import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../contexts/ToastContext';
import photoService from '../services/photoService';
import { useAppDispatch } from '../store/hooks';
import { deletePhoto, fetchPhotos, fetchPhotoStats } from '../store/slices/photosSlice';
import { PhotoCategory, ProjectPhoto } from '../types';

/**
 * Optimized photo operations hook with memoized callbacks
 * Reduces re-renders and provides consistent photo operation handlers
 */
export const useOptimizedPhotoOperations = (projectId?: string) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const toast = useToast();

  // Memoized category color mapping for better performance
  const categoryColors = useMemo(
    () => ({
      progress: 'bg-brand-100 text-brand-800',
      safety: 'bg-red-100 text-red-800',
      inspection: 'bg-yellow-100 text-yellow-800',
      material_delivery: 'bg-green-100 text-green-800',
      issue: 'bg-red-100 text-red-800',
      quality_control: 'bg-purple-100 text-purple-800',
      before_after: 'bg-indigo-100 text-indigo-800',
      documentation: 'bg-gray-100 text-gray-800',
      completion: 'bg-green-100 text-green-800',
      other: 'bg-gray-100 text-gray-800',
    }),
    []
  );

  // Memoized category info getter
  const getCategoryInfo = useCallback(
    (category: PhotoCategory) => ({
      value: category,
      label: t(`photos.categories.${category}`),
      color: categoryColors[category] || categoryColors.other,
    }),
    [categoryColors, t]
  );

  // Memoized date formatter
  const formatDate = useCallback((dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  }, []);

  // Optimized delete handler with proper error handling
  const handleDelete = useCallback(
    async (photo: ProjectPhoto) => {
      if (!window.confirm(t('photos.confirmations.deletePhoto', { title: photo.title }))) {
        return;
      }

      try {
        const result = await dispatch(
          deletePhoto({
            photoId: photo.photo_id,
            projectId,
          })
        ).unwrap();

        if (result.success) {
          toast.success(t('photos.toast.deleteSuccess'));
          // Refresh data after deletion
          dispatch(
            fetchPhotos({
              query: {}, // Use current query state
              projectId,
            })
          );
          dispatch(fetchPhotoStats(projectId));
        } else {
          throw new Error(result.message);
        }
      } catch (error: any) {
        console.error('Delete failed:', error);
        toast.error(error.message || t('photos.toast.deleteFailed'));
      }
    },
    [dispatch, projectId, toast, t]
  );

  // Optimized download handler
  const handleDownload = useCallback(
    async (photo: ProjectPhoto) => {
      try {
        await photoService.downloadPhoto(photo.photo_id, `${photo.title}.jpg`, projectId);
        toast.success(t('photos.toast.downloadSuccess'));
      } catch (error) {
        console.error('Download failed:', error);
        toast.error(t('photos.toast.downloadFailed'));
      }
    },
    [projectId, toast, t]
  );

  // Optimized version history handler
  const handleVersionHistory = useCallback((taggingId: string) => {
    // This would open version history modal/drawer
    console.log('Version history for:', taggingId);
    // TODO: Implement version history functionality
  }, []);

  // Optimized annotation toggle handler
  const handleAnnotationToggle = useCallback((showAnnotationMode: boolean) => {
    console.log('Toggle annotation mode:', !showAnnotationMode);
    // TODO: Implement annotation mode functionality
    return !showAnnotationMode;
  }, []);

  return {
    getCategoryInfo,
    formatDate,
    handleDelete,
    handleDownload,
    handleVersionHistory,
    handleAnnotationToggle,
    categoryColors,
  };
};
